import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import PatientHistoryDialog from "@/components/PatientHistoryDialog";
import ReviewTag from "@/components/ReviewTag";
import { CheckCheck, Search, X, Star, MessageSquarePlus, Ban, BadgeCheck } from "lucide-react";

export default function CompletedSheet() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [historyName, setHistoryName] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sheets/completed"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((e) => e.patient_name.toLowerCase().includes(s));
  }, [rows, q]);

  const askForReview = async (id) => {
    setPendingId(id);
    try {
      const { data } = await api.post(`/entries/${id}/review/ask`, {
        note: "Google Review Link Sent",
      });
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Google review link marked as sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingId(null);
    }
  };

  const skipReview = async (id) => {
    setPendingId(id);
    try {
      const { data } = await api.post(`/entries/${id}/review/skip`);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Marked as \"Did not ask for review\"");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingId(null);
    }
  };

  const alreadyReviewed = async (id) => {
    setPendingId(id);
    try {
      const { data } = await api.post(`/entries/${id}/review/already-reviewed`);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Marked as \"Already Reviewed\"");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
          <CheckCheck size={18} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Archive of finished treatments</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Completed Treatment</h1>
        </div>
      </div>

      <Card className="mt-3 border-slate-200 p-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 pr-9" placeholder="Search patient name in this sheet..."
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="completed-search-input" />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="completed-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
              <X size={14} />
            </button>
          )}
        </div>
      </Card>

      <Card className="mt-3 mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-190px)] [&>div]:overflow-auto" data-testid="completed-sheet">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="completed-empty">
            {q ? `No matches for "${q}".` : "Nothing completed yet."}
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14">S.No.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Review status</TableHead>
                <TableHead className="text-right">Review action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => {
                const isTreatment = e.status === "treatment_completed";
                const needsChoice = isTreatment && !e.review_state;
                const busy = pendingId === e.id;
                return (
                  <TableRow key={e.id} data-testid={`completed-row-${e.id}`}>
                    <TableCell className="text-slate-500 text-sm w-14" data-testid={`completed-sno-${e.id}`}>{i + 1}</TableCell>
                    <TableCell className="text-slate-700 whitespace-nowrap" data-testid={`completed-date-${e.id}`}>
                      {fmtDate(e.entry_date)}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      <button type="button" onClick={() => setHistoryName(e.patient_name)}
                              data-testid={`completed-name-${e.id}`}
                              className="hover:text-slate-700 hover:underline text-left">
                        {e.patient_name}
                      </button>
                    </TableCell>
                    <TableCell data-testid={`completed-review-status-${e.id}`}>
                      {e.review_state
                        ? <ReviewTag state={e.review_state} testid={`completed-review-tag-${e.id}`} />
                        : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {needsChoice ? (
                        <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                disabled={busy}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`ask-review-btn-${e.id}`}
                              >
                                <Star size={14} className="mr-1" /> Ask for Review
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              <DropdownMenuLabel className="text-xs text-slate-500">
                                Select an action
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onSelect={() => askForReview(e.id)}
                                data-testid={`ask-review-option-sent-${e.id}`}
                                className="cursor-pointer"
                              >
                                <MessageSquarePlus size={14} className="mr-2 text-emerald-600" />
                                Google Review Link Sent
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            size="sm"
                            onClick={() => skipReview(e.id)}
                            disabled={busy}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid={`skip-review-btn-${e.id}`}
                          >
                            <Ban size={14} className="mr-1" /> Do Not Ask for Review
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => alreadyReviewed(e.id)}
                            disabled={busy}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`already-reviewed-btn-${e.id}`}
                          >
                            <BadgeCheck size={14} className="mr-1" /> Already Reviewed
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400" data-testid={`completed-noaction-${e.id}`}>
                          {isTreatment ? "" : "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <PatientHistoryDialog
        open={!!historyName}
        onOpenChange={(v) => !v && setHistoryName(null)}
        patientName={historyName}
      />
    </div>
  );
}
